import React from 'react';
import { MapPin, Calendar, X, Check } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '../../../components/dialog';
import StaticMap from './StaticMap';
import CivicPointsLogo from '../../../assets/icons/civicpoints_logo.svg';
import type { PublicEventItem } from '../types/event.types';

interface RegistrationResult {
    type: 'success' | 'already_registered' | 'closed' | null;
    message: string;
    userMessage: string;
    email?: string;
}

interface RegistrationResultModalProps {
    registrationResult: RegistrationResult | null;
    onClose: () => void;
    event: PublicEventItem | null;
    onDownloadApp: () => void;
    formatFullDateTime: (dateString: string, timeString: string, timeZone?: string | null) => string;
}

const RegistrationResultModal: React.FC<RegistrationResultModalProps> = ({
    registrationResult,
    onClose,
    event,
    onDownloadApp,
    formatFullDateTime
}) => {
    const handleAddToCalendar = () => {
        if (!event || !event.event_date || !event.start_time) {
            console.error('Missing event data:', { event, event_date: event?.event_date, start_time: event?.start_time });
            return;
        }

        // Parse event date and time
        const startDate = new Date(event.event_date);
        const startTime = new Date(event.start_time);
        
        if (isNaN(startDate.getTime()) || isNaN(startTime.getTime())) {
            console.error('Invalid date format:', { event_date: event.event_date, start_time: event.start_time });
            return;
        }
        
        // Combine date and time
        const eventStart = new Date(startDate);
        eventStart.setHours(startTime.getHours());
        eventStart.setMinutes(startTime.getMinutes());
        eventStart.setSeconds(0);
        
        // Calculate end time (default to 2 hours after start if no end_time)
        const eventEnd = event.end_time 
            ? (() => {
                const endTime = new Date(event.end_time);
                const end = new Date(startDate);
                end.setHours(endTime.getHours());
                end.setMinutes(endTime.getMinutes());
                end.setSeconds(0);
                return end;
            })()
            : new Date(eventStart.getTime() + 2 * 60 * 60 * 1000); // 2 hours default

        // Format dates for Google Calendar (YYYYMMDDTHHmmss)
        const formatGoogleCalendarDate = (date: Date): string => {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            const seconds = String(date.getSeconds()).padStart(2, '0');
            return `${year}${month}${day}T${hours}${minutes}${seconds}`;
        };

        // Detect if mobile device
        const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

        if (isMobile) {
            // On mobile, use Google Calendar URL which will open in app or browser
            const startDateStr = formatGoogleCalendarDate(eventStart);
            const endDateStr = formatGoogleCalendarDate(eventEnd);
            
            const googleCalendarUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(event.title || 'Event')}&dates=${startDateStr}/${endDateStr}&details=${encodeURIComponent(event.description || '')}&location=${encodeURIComponent(event.location || '')}`;
            
            window.open(googleCalendarUrl, '_blank');
        }
    };
    return (
        <Dialog open={!!registrationResult} onOpenChange={(open) => {
            if (!open) onClose();
        }}>
            <DialogContent 
                className="fixed inset-0 top-0 bottom-0 left-0 right-0 translate-y-0 translate-x-0 max-w-full w-full h-full rounded-none p-0 m-0 [&>button]:hidden"
                dialogOverlayClassName="bg-black/50"
                style={{ zIndex: 100 }}
            >
                <DialogTitle className="sr-only">
                    {registrationResult?.type === 'success' && 'Registration Confirmed!'}
                    {registrationResult?.type === 'already_registered' && 'Good news! You\'re already registered'}
                    {registrationResult?.type === 'closed' && 'Registration Closed'}
                </DialogTitle>
                <div className="flex flex-col h-full bg-[#F0FDF4] min-h-0">
                    {/* Scrollable Content */}
                    <div className="flex-1 overflow-y-auto min-h-0 flex flex-col">
                        {/* Header */}
                        <div className="px-6 pt-6 pb-4">
                            <div className="flex items-center justify-between">
                                <img src={CivicPointsLogo} alt="CivicPoints" className="h-5" />
                                <button
                                    onClick={onClose}
                                    className="text-gray-400 hover:text-gray-600 z-10 relative"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>
                        </div>

                        {/* Registration Closed Case */}
                        {registrationResult?.type === 'closed' && (
                            <div className="px-6 pb-6 text-center">
                                <h2 className="text-2xl font-bold text-gray-900 mb-3">
                                    {registrationResult.message}
                                </h2>
                                <p className="text-gray-600 mb-8">
                                    {registrationResult.userMessage}
                                </p>
                            </div>
                        )}

                        {/* Success and Already Registered Cases */}
                        {(registrationResult?.type === 'success' || registrationResult?.type === 'already_registered') && (
                            <>
                                {/* Confirmation Icon and Message */}
                                <div className="px-6 pb-6 text-center">
                                    <div className="flex justify-center mb-4">
                                        <div className="w-20 h-20 rounded-full bg-[#00C49A] flex items-center justify-center">
                                            <Check className="w-10 h-10 text-white" strokeWidth={3} />
                                        </div>
                                    </div>
                                    <h2 className="text-xl font-bold text-gray-900 mb-3">
                                        {registrationResult.message}
                                    </h2>
                                    <p className="text-sm text-gray-600 mb-4">
                                        {registrationResult.userMessage}
                                    </p>

                                    {/* Action Links for Already Registered */}
                                    {registrationResult.type === 'already_registered' && (
                                        <div className="space-y-2 mb-6">
                                            <button className="text-[#00C49A] underline text-sm">
                                                Resend confirmation email
                                            </button>
                                            <div>
                                                <button className="text-gray-500 underline text-sm">
                                                    Cancel my registration
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Event Details Section */}
                                {event && (
                                    <div className="px-6 py-6 mb-6">
                                        <h3 className="text-xl font-bold text-gray-900 mb-3">
                                            {event.title}
                                        </h3>
                                        <div className="space-y-2 mb-4">
                                            <div className="flex items-center gap-2 text-sm text-gray-600">
                                                <Calendar className="w-4 h-4" />
                                                <span>{formatFullDateTime(event.event_date, event.start_time, event.time_zone)}</span>
                                            </div>
                                            <div className="flex items-center gap-2 text-sm text-gray-600">
                                                <MapPin className="w-4 h-4" />
                                                <span>{event.location || 'Location TBD'}</span>
                                            </div>
                                        </div>

                                        {/* Map */}
                                        {event.latitude && event.longitude && (
                                            <StaticMap
                                                latitude={event.latitude}
                                                longitude={event.longitude}
                                                location={event.location || undefined}
                                            />
                                        )}

                                        {/* Add to Calendar Button */}
                                        <button
                                            onClick={handleAddToCalendar}
                                            className="w-full h-[55px] rounded-xl bg-[#004231] text-white font-semibold text-lg shadow-lg hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
                                        >
                                            <Calendar className="w-5 h-5" />
                                            Add to Calendar
                                        </button>
                                    </div>
                                )}
                            </>
                        )}

                        {/* Spacer to push Download App Section to bottom */}
                        <div className="flex-1"></div>

                        {/* Download App Section - At Bottom of Content */}
                        <div className="px-4 pb-6 mt-6 flex-shrink-0">
                            <div 
                                className="mx-auto text-center"
                                style={{ 
                                    width: '358px',
                                    height: '309.3px',
                                    padding: '25px 15px',
                                    backgroundColor: 'hsla(160, 65%, 47%, 1)',
                                    borderRadius: '16px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '20px',
                                    justifyContent: 'center'
                                }}
                            >
                                {/* Logo */}
                                <div className="flex justify-center">
                                    <div className="w-16 h-16 bg-white rounded-lg flex items-center justify-center">
                                        <img src="/favicon.ico" alt="CivicPoints" className="h-10 w-10" />
                                    </div>
                                </div>
                                
                                {/* Text */}
                                <div className="space-y-2">
                                    <p className="text-base font-bold" style={{ color: 'hsla(165, 100%, 13%, 1)' }}>
                                        Want to earn rewards for attending events?
                                    </p>
                                    <p className="text-sm text-white">
                                        Download the CivicPoints app and start earning today!
                                    </p>
                                </div>
                                
                                {/* Download Button */}
                                <button
                                    onClick={onDownloadApp}
                                    className="bg-white text-lg font-semibold hover:opacity-90 transition-opacity w-full h-[65px] rounded-xl"
                                    style={{ 
                                        color: '#004231',
                                        padding: '12px 24px'
                                    }}
                                >
                                    Download the app
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default RegistrationResultModal;

